import { JarvisProduct } from '@vfde-sails/avc-core';
import { getCMSConfig } from '../../../util/cms';
import { getPriceWithRegioCharge } from '../../../util/regioCharge';
import { MetacodePrefix } from '../../../enums/product';

/**
 * Helper function to handle price updates with regio charge.
 * This function is not moved into the avc-core because the regio-charge handling
 * is different in the individual applications.
 *
 * @param newProduct
 * @returns
 */
const getNewTariffPrices = (
    newProduct: JarvisProduct,
): Partial<JarvisProduct> => {
    const regioCharge = newProduct.regioCharge || +getCMSConfig().regioFeeBenefit.minimumChargeEuro;

    const monthlyPrice = +newProduct.price.monthly;
    const discountedPrice = newProduct.discount.gross;

    return {
        discount: {
            ...newProduct.discount,
            gross: discountedPrice > 0 ? getPriceWithRegioCharge(discountedPrice, regioCharge) : 0,
        },
        price: {
            ...newProduct.price,
            monthly: getPriceWithRegioCharge(monthlyPrice, regioCharge),
        },
    };
};

/**
 * Handle tariffs price correction in the step before the avc check.
 * @param tariffs
 * @returns Updated tariffs
 */
export const updateTariffPricesBeforeAvc = (tariffs: JarvisProduct[]): JarvisProduct[] => {
    const updatedTariffs: JarvisProduct[] = [];

    for (const tariff of tariffs) {
        updatedTariffs.push({
            ...tariff,
            ...getNewTariffPrices(tariff),
        });
    }

    return updatedTariffs;
};

/**
 * Updates the tariff properties based on the tariffs from the result of the availabilitycheck.
 *
 * @param currentTariffs Tariffs from state
 * @param afterAvcTariffs Tariffs from the availabilitycheck
 * @returns {JarvisProduct[]} Updated tariffs
 */
export const updateTariffsWithAvcResult = (
    currentTariffs: JarvisProduct[],
    afterAvcTariffs?: JarvisProduct[]
) => {
    const updatedTariffs: JarvisProduct[] = [];

    if (!afterAvcTariffs) {
        return currentTariffs;
    }

    for (const tariff of currentTariffs) {
        const newTariff = afterAvcTariffs.find(p => tariff.id === p.id);

        newTariff && updatedTariffs.push({
            ...tariff,
            afterVpProductInfo: newTariff.productInfo,
            ...getNewTariffPrices(newTariff),
        });
    }

    return updatedTariffs;
};

/**
 * Goes through the pre-fiber tariff ids in CMS config
 * and checks if the recommended tariff id is amongst the pre-fib ids.
 *
 * @param recommendedTariffId Id of the Recommended tariff by Jarvis
 * @returns {boolean} true when specifically pre-fiber tariff is available
 */
export const isPreFiberAvailable = (recommendedTariffId: string | undefined): boolean => {
    if (!recommendedTariffId) {
        return false;
    }

    const preFiberIds = Object.values(getCMSConfig().tariffIds);

    for (const preFiberId of preFiberIds) {
        if (preFiberId === recommendedTariffId) {
            return true;
        }
    }

    return false;
};

export const filterOnlyCableMetacodes = (metacodes: string[]) =>
    metacodes.filter(id => id.toLowerCase().includes(MetacodePrefix.CABLE));
